import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    ViewChild,
    inject
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { InputComponent } from "@components/input/input.component";
import { NgbPopover } from "@ng-bootstrap/ng-bootstrap";
import { groupBy } from "lodash";
import { Subject, Subscription } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { BusyService } from "~app/common/components/busy/busy.service";
import { ToastService } from "~app/common/components/toast/toast.service";
import { CommandService } from "~app/common/services/cqs/command.service";
import { CommonTexts } from "~app/common/texts/common.texts";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { IValidationContext } from "~app/common/validation/IValidationContext";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { AddProduktionsmallCommand } from "~app/features/planera/commands/AddProduktionsmallCommand";
import { AddProduktionsmallHandelssortimentCommand } from "~app/features/planera/commands/AddProduktionsmallHandelssortimentCommand";
import { AddProduktionsmallProduktCommand } from "~app/features/planera/commands/AddProduktionsmallProduktCommand";
import { AddProduktionsmallProduktinstruktionCommand } from "~app/features/planera/commands/AddProduktionsmallProduktinstruktionCommand";
import { IArbetsorderDetail } from "~app/features/planera/model/IArbetsorderDetail";
import { IProduktvalRow } from "../../produktval/produktval.models";
import { ArbetsorderProduktvalProduktionsmallTexts } from "./arbetsorder-produktval-produktionsmall.texts";
import { ArbetsorderProduktvalProduktionsmallValidation } from "./arbetsorder-produktval-produktionsmall.validation";

@Component({
    selector: "rask-arbetsorder-produktval-produktionsmall",
    templateUrl: "./arbetsorder-produktval-produktionsmall.component.html",
    styleUrls: ["./arbetsorder-produktval-produktionsmall.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ButtonComponent,
        NgbPopover,
        FormsModule,
        ReactiveFormsModule,
        InputComponent
    ]
})
export class ArbetsorderProduktvalProduktionsmallComponent implements OnDestroy {
    @Input() arbetsorder: IArbetsorderDetail;
    texts = ArbetsorderProduktvalProduktionsmallTexts.texts;
    commonTexts = CommonTexts.texts;
    editForm = new UntypedFormGroup({});
    formSubscription: Subscription;
    mall: { benamning: string };
    @ViewChild("popOver") popover: NgbPopover;

    // eslint-disable-next-line @angular-eslint/no-output-rename, @angular-eslint/no-output-on-prefix
    @Output("onProduktionsMallAddedEvent") produktionsMallAddedEvent = new EventEmitter();
    @Input() selectedProduktval: IProduktvalRow[];
    validationContext: IValidationContext;

    private readonly busyService = inject(BusyService);
    private readonly commandService = inject(CommandService);
    private readonly destroy$ = new Subject<void>();
    private readonly formBuilder = inject(UntypedFormBuilder);
    private readonly toastService = inject(ToastService);

    constructor() {
        this.mall = {
            benamning: undefined
        };
    }

    get canBeSaved(): boolean {
        return this.validationContext.validate();
    }

    closePopover() {
        if (this.popover.isOpen()) {
            this.popover.close();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    openPopover(): void {
        if (!this.popover.isOpen()) {
            this.validationContext = ArbetsorderProduktvalProduktionsmallValidation.init(this.mall, this.selectedProduktval);

            this.editForm = this.formBuilder.group({
                benamning: this.formBuilder.control(this.mall.benamning)
            });

            this.formSubscription?.unsubscribe();
            this.formSubscription = this.editForm.get("benamning").valueChanges.pipe(
                takeUntil(this.destroy$)
            ).subscribe((newNamn: string) => this.mall.benamning = newNamn);
            this.popover.open();
        }
    }

    saveAsMall() {
        if (this.validationContext.hasErrors(ValidationScopes.spara)) {
            return;
        }

        this.createProduktionsMallCommand(this.mall.benamning);

        this.busyService
            .addWork(this.commandService.pushCommandQueueTransaction())
            .subscribe((response) => {
                if (response) {
                    this.toastService.add(`Produktionsmallen '${this.mall.benamning}' skapades`);
                    this.editForm.get("benamning").reset();
                    this.editForm.markAsPristine();
                    this.produktionsMallAddedEvent.emit();
                    this.closePopover();
                }
            });
    }

    private createProduktionsMallCommand(benamning: string) {
        const produktionsmallGuid = GuidUtils.newGuid();
        this.commandService.addTransactionCommand(new AddProduktionsmallCommand(produktionsmallGuid, benamning, this.arbetsorder.vardekap));

        const produktvalGrupperad = groupBy(this.selectedProduktval, p => p.handelssortimentId);
        for (const key in produktvalGrupperad) {
            if (Object.hasOwn(produktvalGrupperad, key)) {
                const prodval = produktvalGrupperad[key];
                // Handelssortiment
                this.commandService.addTransactionCommand(new AddProduktionsmallHandelssortimentCommand(produktionsmallGuid, Number(key)));
                prodval.forEach(x => {
                    if (x.produktId) {
                        this.commandService.addTransactionCommand(new AddProduktionsmallProduktCommand(produktionsmallGuid, Number(key), x.produktId));
                        x.produktinstruktioner.forEach(y => {
                            this.commandService.addTransactionCommand(
                                new AddProduktionsmallProduktinstruktionCommand(
                                    produktionsmallGuid,
                                    Number(key),
                                    x.produktId,
                                    y.id,
                                    x.medTillUtbytessimulering === y.id ? true : false));
                        });
                    }
                });
            }
        }
    }
}
